/**
 * The selection a chase-binder picking session is holding.
 *
 * Chase's word for the flow is "comb through", and combing means the search
 * changes under you: type "charizard", take three, sort by value, take two
 * more, search "magikarp", take another. So the selection cannot live inside
 * the results, or refining a search would silently empty it - the one bug that
 * would make this feature not worth having.
 *
 * Hence a Map held above the grid, and hence it keeps the whole card row rather
 * than the id alone. When somebody searches again, the row they picked four
 * queries ago is no longer on screen anywhere, and the insert still needs its
 * name, its set and its picture.
 *
 * KEYED ON THE CATALOGUE ID - `set_id-local_id`, as it arrives on a
 * catalog_browse row. Deliberately NOT the `public.cards` row uuid the
 * collection's own select mode keys on. Those are two id spaces: a catalogue id
 * names a card that exists, a row uuid names a copy somebody owns, and a chase
 * binder is a list of cards nobody owns yet. Mixing them would write a uuid
 * into wish_binder_cards.tcgdex_card_id, which is the column every other wish
 * surface matches a catalogue id against.
 *
 * Insertion order is the pick order, which is what Map gives for free and what
 * the binder's positions are written from.
 */

import { canWishListCard } from '../../utils/tcgPocket';
import { TOP_TEN_LABEL } from '../../services/wishList';

/** A fresh, empty selection. */
export function emptySelection() {
  return new Map();
}

/**
 * Can this card be picked at all?
 *
 * TCG Pocket cards cannot. They are free inside the Pocket app and were never
 * printed, so no scan can ever match one and no acquisition door can ever fill
 * the slot - a chase binder holding one would be a promise the app cannot keep.
 * Same rule, same helper, as every other wish entry point.
 */
export function isPickable(card) {
  return Boolean(card && card.id) && canWishListCard(card);
}

/** Why a card refused to be picked, for the toast. Null when it did not. */
export function refusalFor(card) {
  if (!card || !card.id) return 'That card is missing its catalogue id, so it cannot be added.';
  if (!canWishListCard(card)) {
    return 'TCG Pocket cards are free inside the Pocket app and were never printed, so they cannot go on a chase list.';
  }
  return null;
}

/**
 * Every card in a list that a wish surface can actually take.
 *
 * A wish picker HIDES what it cannot accept rather than refusing it at the tap.
 * Refusing meant somebody hunting a grid for a card the app was never going to
 * let them have, and a Pocket card is exactly that: free in the Pocket app,
 * never printed, so no scan and no trade can ever fill the slot. Same predicate
 * as the refusal above, so the screen and the rule cannot disagree.
 *
 * Only the wish contexts do this. Explore itself keeps showing Pocket cards
 * with their Free chip, because browsing them is the point there.
 */
export function pickableCards(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(isPickable);
}

/** The same cut over a list of SETS, for the set hits a picker offers. */
export function pickableSets(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((set) => canWishListCard({ set_id: set?.id }));
}

/**
 * Tap a card: in if it was out, out if it was in.
 *
 * Returns a NEW Map, never the one passed in, so React sees a change. An
 * unpickable card comes back as the same selection it went in with, which is
 * what lets a caller tell "nothing happened" from "something was added".
 */
export function toggleCard(selection, card) {
  if (!isPickable(card)) return selection;
  const next = new Map(selection);
  if (next.has(card.id)) next.delete(card.id);
  else next.set(card.id, minimalCard(card));
  return next;
}

/**
 * What is actually kept per card: enough to write the binder row and draw the
 * card in it, and nothing else. A whole catalog_browse row carries rarity, HP,
 * types and era columns that a chase binder has no use for.
 */
function minimalCard(card) {
  return {
    id: card.id,
    name: card.name || null,
    set_id: card.set_id || null,
    set_name: card.set_name || null,
    local_id: card.local_id || null,
    image_url: card.image_url || null,
    near_mint: card.near_mint ?? null,
  };
}

/** The ids, as CatalogCardGrid wants them: a Set it can ask `has` of. */
export function selectedIdSet(selection) {
  return new Set(selection.keys());
}

/** The picked cards, in the order they were picked. */
export function selectedCards(selection) {
  return [...selection.values()];
}

/**
 * How the picked cards divide against a binder's current contents.
 *
 * `already` is not an error and is not filtered out of the count on the bar:
 * somebody who picked twelve cards should be told twelve, and told that two of
 * them are already in there, rather than shown ten and left to wonder.
 *
 * @param {Map} selection
 * @param {Set<string>|null} inBinder catalogue ids the binder already holds,
 *   or null while that has not been read yet - which must claim nothing.
 */
export function splitAgainstBinder(selection, inBinder) {
  const cards = selectedCards(selection);
  if (!inBinder) return { total: cards.length, already: 0, fresh: cards.length };
  const already = cards.filter((c) => inBinder.has(c.id)).length;
  return { total: cards.length, already, fresh: cards.length - already };
}

/**
 * Where a picking session is sending its cards.
 *
 * The picker serves two destinations now: a chase binder, which takes as many
 * cards as somebody cares to comb out, and the ranked ten, which has room for
 * exactly as many as it has free slots. So the destination is a value the
 * picker is given rather than a mode it branches on, and the capacity travels
 * with it - the number comes from the caller, which reads it from wishList.js,
 * so the cap is still only written down once.
 *
 * @param {{name: string, capacity?: number|null}} destination
 */
export function destinationName(destination) {
  return destination?.name || 'your chase list';
}

/** The ranked ten, named the way the rest of the app names it. */
export const TOP_TEN_DESTINATION_NAME = `your ${TOP_TEN_LABEL}`;

/**
 * How many more cards this destination will take, or null for as many as you
 * like. Clamped at zero so a list that somehow holds more than the cap reports
 * no room rather than negative room.
 */
export function roomLeft(destination) {
  const capacity = destination?.capacity;
  if (capacity === null || capacity === undefined) return null;
  const n = Number(capacity);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}

/**
 * Whether one more card fits.
 *
 * Asked BEFORE the tap is honoured, because a cap that only speaks at the end
 * lets somebody comb out fifteen cards for a list with three slots and then
 * refuses the lot. The count that matters is what is already picked, not what
 * the ten already holds - the free-slot arithmetic is the caller's, done once.
 */
export function fitsAnother(selection, destination) {
  const room = roomLeft(destination);
  if (room === null) return true;
  return selection.size < room;
}

/**
 * Said when a tap did not land because the destination is out of room. Names
 * the number and the way out, since the way out is not obvious: the cards
 * already picked are what is in the way.
 */
export function capRefusal(destination) {
  const room = roomLeft(destination);
  const name = destinationName(destination);
  if (room === 0) return `${name} is full. Remove a card from it to add another.`;
  const slots = room === 1 ? '1 slot' : `${room} slots`;
  return `That is all ${slots} ${name} has room for. Tap one you picked to drop it and choose another.`;
}

/**
 * How much room is left, for the header, so the ceiling is visible the whole
 * time rather than arriving as a refusal. Null for a destination with no cap,
 * where there is nothing worth saying.
 */
export function roomNote(selection, destination) {
  const room = roomLeft(destination);
  if (room === null) return null;
  if (room === 0) return `${destinationName(destination)} is full.`;
  if (selection.size === 0) return `Room for ${room} more.`;
  const left = Math.max(0, room - selection.size);
  if (left === 0) return `That fills ${destinationName(destination)}.`;
  return `${left} more ${left === 1 ? 'slot' : 'slots'} after these.`;
}

/**
 * What to say after writing to the ranked ten.
 *
 * `full` is the cap answering from the database, which can happen even after
 * the client's own arithmetic said there was room - another device, or a parent
 * on a child's list. So it is reported as a fact about the ten rather than as a
 * failure.
 */
export function addedToTopTenMessage({ added = 0, full = 0, failed = 0 } = {}) {
  const parts = [];
  if (added > 0) parts.push(`${added} ${added === 1 ? 'card' : 'cards'} added to ${TOP_TEN_LABEL}.`);
  if (full > 0) parts.push(`${TOP_TEN_LABEL} filled up, so ${full} ${full === 1 ? 'card' : 'cards'} did not go in.`);
  if (failed > 0) parts.push(`${failed} could not be added.`);
  if (parts.length === 0) return `Nothing was added to ${TOP_TEN_LABEL}.`;
  return parts.join(' ');
}

/** "Add 3 cards to Magikarps". One card is a card, not 1 cards. */
export function addLabel(count, binderName) {
  const noun = count === 1 ? 'card' : 'cards';
  return `Add ${count} ${noun} to ${binderName}`;
}

/** What to say after the write, counting what was already there honestly. */
export function addedMessage({ added, already }) {
  if (added === 0 && already > 0) {
    return already === 1
      ? 'That card was already in this binder.'
      : `All ${already} of those were already in this binder.`;
  }
  const head = `${added} ${added === 1 ? 'card' : 'cards'} added.`;
  if (already === 0) return head;
  return `${head} ${already} ${already === 1 ? 'was' : 'were'} already in here.`;
}
